import React, { useEffect, useMemo, useRef, useState } from 'react';

import AlertTriangle from '@crehana/compass.icons.outline/AlertTriangle';
import ArrowRight from '@crehana/compass.icons.outline/ArrowRight';
import Refresh from '@crehana/compass.icons.outline/Refresh';
import { Button, Paginator, Skeleton } from '@crehana/compass.ui/v2';
import { CREHANA_BASE_URL_PRD } from '@crehana/constants';
import { useTranslation } from '@crehana/i18n';
import { CustomNextImage } from '@crehana/next';
import clsx from 'clsx';
import parseISO from 'date-fns/parseISO';

import { SocialHubNewsStatusEnum } from '@/generated/graphql/types';

import { useCompleteNewsVisualization } from '../hooks/use-complete-news-visaulization';
import {
  type TNews,
  useGetSocialHubNews,
} from '../hooks/use-get-socia-hub-news';
import { PreviewDocumentDialog } from './preview-document-dialog';

type TDocumentFile = NonNullable<
  NonNullable<ReturnType<typeof useGetSocialHubNews>['news'][number]['file']>
>;

type TSocialHubCardProps = {
  centralizedOrganizationId: number;
};

type LayoutSize = 'small' | 'medium' | 'large';

export const SocialHubCard: React.FC<TSocialHubCardProps> = ({
  centralizedOrganizationId,
}) => {
  const { t, languageCode } = useTranslation();
  const { news, loading, error, refetch } = useGetSocialHubNews(
    centralizedOrganizationId,
  );
  const { completeNewsVisualization } = useCompleteNewsVisualization(
    centralizedOrganizationId,
  );
  const [newsPage, setNewsPage] = useState(1);
  const [openNews, setOpenNews] = useState<TDocumentFile | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [layoutSize, setLayoutSize] = useState<LayoutSize>('small');

  const currentNews = news[newsPage - 1];
  const isSmallLayout = useMemo(() => layoutSize === 'small', [layoutSize]);
  const isMediumOrLargeLayout = useMemo(
    () => layoutSize !== 'small',
    [layoutSize],
  );
  const imageUrl = useMemo(
    () => currentNews?.front_page_image_info?.file?.absolute_url,
    [currentNews?.front_page_image_info?.file?.absolute_url],
  );

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && containerRef.current.offsetWidth > 0) {
        const width = containerRef.current.offsetWidth;
        if (width >= 1088) {
          setLayoutSize('large');
        } else if (width >= 536) {
          setLayoutSize('medium');
        } else {
          setLayoutSize('small');
        }
      }
    };

    const checkAndSetSize = () => {
      if (containerRef.current && containerRef.current.offsetWidth > 0) {
        handleResize();
      } else {
        requestAnimationFrame(checkAndSetSize);
      }
    };

    checkAndSetSize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return <SkeletonLayout layoutSize={layoutSize} />;
  }

  if (error) {
    return (
      <CardLayout>
        <div className="tw-space-y-1.2 tw-flex tw-flex-col tw-items-center tw-justify-center">
          <div className="tw-h-5.6 tw-w-5.6 tw-flex tw-items-center tw-justify-center tw-bg-alert-100 tw-rounded-full">
            <div className="tw-h-4.8 tw-w-4.8 tw-bg-alert-200 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-alert-500">
              <AlertTriangle size={24} className="tw-text-alert-500" />
            </div>
          </div>
          <div className="tw-space-y-0.4 tw-text-center">
            <h3 className="tw-font-subtitle5">
              {t('SOCIAL_HUB_CARD_ERROR_TITLE')}
            </h3>
            <p className="tw-font-body5 tw-text-neutral-light-700">
              {t('SOCIAL_HUB_CARD_ERROR_DESCRIPTION')}
            </p>
          </div>
          <Button type="button" onClick={() => refetch().catch()}>
            <Refresh size={16} className="tw-text-inherit" />
            {t('SOCIAL_HUB_CARD_ERROR_CTA')}
          </Button>
        </div>
      </CardLayout>
    );
  }

  if (news.length === 0) {
    return (
      <CardLayout>
        <div className="tw-space-y-1.2 tw-flex tw-flex-col tw-items-center tw-justify-center">
          <div className="tw-h-[10.4rem] tw-w-[10.4rem] tw-bg-neutral-light-300 tw-rounded-full tw-flex tw-items-center tw-justify-center">
            <CustomNextImage
              src="https://crehana-vercel-images-prd.imgix.net/public/lx-mf/1f6d027c-2990-4382-8519-0007f2fbbf38-widgets-hris-(2).png?auto=format&fit=clip"
              alt="Empty news"
              className="tw-object-cover"
              width={104}
              height={102}
            />
          </div>
          <div className="tw-space-y-0.4 tw-text-center">
            <h3 className="tw-font-subtitle5">
              {t('SOCIAL_HUB_CARD_EMPTY_TITLE')}
            </h3>
            <p className="tw-font-body5 tw-text-neutral-light-700 tw-w-[38rem]">
              {t('SOCIAL_HUB_CARD_EMPTY_DESCRIPTION')}
            </p>
            <Button
              type="button"
              className="!tw-mt-1.2"
              onClick={() => {
                window.open(`${CREHANA_BASE_URL_PRD}/blog/`, '_blank');
              }}
            >
              {t('SOCIAL_HUB_CARD_EMPTY_BUTTON')}
            </Button>
          </div>
        </div>
      </CardLayout>
    );
  }

  const handleOpenNews = (selectedNews: TNews) => {
    setOpenNews(selectedNews.file);

    if (
      selectedNews.status ===
      SocialHubNewsStatusEnum.SocialHubNewsStatusCompleted
    ) {
      return;
    }

    completeNewsVisualization(selectedNews.id);
  };

  return (
    <>
      <CardLayout ref={containerRef}>
        <div
          className={clsx('tw-flex tw-flex-col tw-w-full tw-h-full', {
            'tw-space-y-2': isSmallLayout,
            'sm:tw-space-y-1.2': isMediumOrLargeLayout,
          })}
        >
          <div
            className={clsx('tw-grow', {
              'tw-flex tw-flex-col tw-gap-1.2': isSmallLayout,
              'tw-flex tw-gap-2.4': isMediumOrLargeLayout,
            })}
          >
            {imageUrl && (
              <div
                className={clsx('tw-overflow-hidden tw-rounded', {
                  'tw-w-full tw-h-[12rem]': isSmallLayout,
                  'tw-object-cover tw-rounded tw-flex tw-shrink-0 tw-w-[28rem] tw-h-[23.8rem]':
                    isMediumOrLargeLayout,
                })}
              >
                <CustomNextImage
                  onClick={() => handleOpenNews(currentNews)}
                  src={imageUrl}
                  className={clsx('tw-object-cover tw-cursor-pointer', {
                    'tw-w-full tw-h-full': isSmallLayout,
                    'tw-w-full tw-h-full tw-rounded': isMediumOrLargeLayout,
                  })}
                  width={isSmallLayout ? 120 : 282}
                  height={isSmallLayout ? 98 : 238}
                  alt={currentNews.title}
                />
              </div>
            )}
            <div
              className={clsx('tw-flex tw-flex-col tw-space-y-0.8', {
                'tw-justify-center': isSmallLayout,
                'tw-justify-center tw-px-2.4 sm:tw-px-0 tw-h-24 tw-pb-1.2':
                  isMediumOrLargeLayout,
                'tw-px-0': isSmallLayout && imageUrl,
                'tw-px-1.2 tw-w-full tw-h-20 tw-justify-center tw-items-center':
                  isSmallLayout && !imageUrl,
                'sm:tw-justify-start': isMediumOrLargeLayout && imageUrl,
                'tw-items-center tw-text-center tw-w-full tw-justify-center':
                  isMediumOrLargeLayout && !imageUrl,
              })}
            >
              <p
                className={clsx(
                  'tw-font-body5 tw-text-neutral-light-600',
                  isSmallLayout && 'tw-hidden',
                )}
              >
                {t('SOCIAL_HUB_CARD_PUBLISH_DATE', {
                  interpolation: {
                    date: parseISO(
                      currentNews.published_date,
                    ).toLocaleDateString(languageCode, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    }),
                  },
                })}
              </p>
              <p
                className={clsx({
                  'tw-font-subtitle4 tw-line-clamp-3': isSmallLayout,
                  'tw-font-subtitle2 sm:tw-font-subtitle4':
                    isMediumOrLargeLayout,
                  'tw-overflow-auto tw-grow': isMediumOrLargeLayout && imageUrl,
                  'tw-max-h-9.6 tw-overflow-y-auto':
                    isMediumOrLargeLayout && !imageUrl,
                })}
              >
                {currentNews.title}
              </p>
              <button
                type="button"
                className={clsx(
                  'tw-flex tw-items-center tw-gap-0.8 tw-text-primary-light-500 tw-font-subtitle5',
                  {
                    'tw-self-start tw-w-fit': isSmallLayout && imageUrl,
                    'tw-w-full tw-justify-center': isSmallLayout && !imageUrl,
                  },
                )}
                onClick={() => handleOpenNews(currentNews)}
              >
                {t('SOCIAL_HUB_CARD_CTA')}
                <ArrowRight
                  size={isSmallLayout ? 16 : 20}
                  className="tw-text-inherit"
                />
              </button>
            </div>
          </div>
          <div
            className={clsx('tw-shrink-0', {
              'tw-flex tw-justify-end': isSmallLayout,
              'tw-justify-end tw-flex tw-flex-items-center':
                isMediumOrLargeLayout,
            })}
          >
            <Paginator
              totalPages={news.length}
              currentPage={newsPage}
              onPageChange={page => setNewsPage(page)}
            />
          </div>
        </div>
      </CardLayout>
      <PreviewDocumentDialog
        document={openNews}
        onClose={() => setOpenNews(null)}
      />
    </>
  );
};

const SkeletonLayout = ({ layoutSize }: { layoutSize: LayoutSize }) => {
  const isSmallLayout = layoutSize === 'small';

  return (
    <CardLayout>
      <div className="tw-w-full tw-h-full">
        <div
          className={clsx('tw-flex tw-gap-2', {
            'tw-flex-col': isSmallLayout,
            'tw-flex-row': !isSmallLayout,
          })}
        >
          {!isSmallLayout && (
            <Skeleton className="tw-h-[23rem] tw-w-[28rem] tw-shrink-0" />
          )}
          {isSmallLayout && (
            <Skeleton className="tw-h-9.6 tw-w-12 tw-shrink-0" />
          )}
          <div className="tw-space-y-0.8 tw-w-full">
            {!isSmallLayout && <Skeleton className="tw-h-1.6 tw-w-12" />}
            <Skeleton
              className={clsx(
                isSmallLayout ? 'tw-h-7.2' : 'tw-h-[14rem]',
                'tw-w-full',
              )}
            />
            <Skeleton
              className={clsx(isSmallLayout ? 'tw-h-1.6' : 'tw-h-4', 'tw-w-8')}
            />
          </div>
        </div>
        <div className="tw-mt-1.2 tw-flex tw-justify-end tw-items-end">
          <Skeleton className="tw-h-2 tw-w-10.4" />
        </div>
      </div>
    </CardLayout>
  );
};

const CardLayout = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
  }
>(({ children }, ref) => {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      className="tw-p-2 tw-border tw-border-solid tw-border-neutral-light-400 tw-rounded-1.6 tw-bg-neutral-light-100 tw-space-y-1.2 tw-h-[35.2rem] tw-flex tw-flex-col"
    >
      <h3 className="tw-font-subtitle5">{t('SOCIAL_HUB_CARD_TITLE')}</h3>
      <div className="tw-grow tw-w-full tw-flex tw-items-center tw-justify-center">
        {children}
      </div>
    </div>
  );
});

CardLayout.displayName = 'CardLayout';
